import { useState } from "react";
import { createQuote } from "@/lib/api";
import { toast } from "sonner";
import { Send, CheckCircle2, Mail, Phone } from "lucide-react";
import { WhatsAppIcon } from "@/components/WhatsApp";

const initial = {
  name: "", email: "", company: "", phone: "",
  project_type: "", message: "",
};

const projectTypes = [
  "LED Video Wall — Indoor", "LED Screen — Outdoor", "LED Rental — Event / Tour",
  "Transparent LED — Storefront", "LCD Video Wall", "LCD Outdoor Signage",
  "Interactive Touch Kiosk", "Broadcast Studio", "Digital Asset / Content", "Other",
];

export const QuoteForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_type || !form.message) {
      toast.error("Please fill the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await createQuote(form);
      setDone(true);
      setForm(initial);
      toast.success("Brief received. Our team will respond within 1 business day.");
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="quote-section" className="relative py-20 lg:py-28 bg-[#F1F3F7] border-t border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-3">Request a Quote</div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0F172A] leading-tight mb-6">
            Tell us about your project.
          </h2>
          <p className="text-[#475569] text-base lg:text-lg leading-relaxed mb-10">
            A specifier replies within one business day. Share the venue, viewing distance, and
            content needs — we’ll come back with an LED or LCD plan that fits.
          </p>

          <div className="space-y-5">
            <ContactItem icon={Mail} label="Email" value="akshay@smartdigi.in" href="mailto:akshay@smartdigi.in" />
            <ContactItem icon={Phone} label="Sales Desk" value="+91 98184 82197" href="tel:+919818482197" />
            <ContactItem icon={WhatsAppIcon} label="WhatsApp" value="Chat with us" href="https://wa.me/919818482197" external />
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid="quote-form"
            onSubmit={submit}
            className="bg-white border border-[#E2E8F0] rounded-xl p-8 lg:p-10 shadow-sm"
          >
            {done && (
              <div className="mb-6 flex items-start gap-3 border border-[#16A34A]/30 bg-[#16A34A]/8 p-4 rounded-md">
                <CheckCircle2 size={22} className="text-[#16A34A] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-[#16A34A]">Brief received</div>
                  <div className="text-sm text-[#475569]">A specifier will be in touch within one business day.</div>
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name *" testid="quote-input-name" value={form.name} onChange={set("name")} />
              <Field label="Work email *" testid="quote-input-email" type="email" value={form.email} onChange={set("email")} />
              <Field label="Company" testid="quote-input-company" value={form.company} onChange={set("company")} />
              <Field label="Phone" testid="quote-input-phone" value={form.phone} onChange={set("phone")} />
              <Select label="Project type *" testid="quote-input-project-type" value={form.project_type} onChange={set("project_type")} options={projectTypes} />
            </div>

            <div className="mt-5">
              <Label>Project brief *</Label>
              <textarea
                data-testid="quote-input-message"
                value={form.message}
                onChange={set("message")}
                rows={5}
                placeholder="Venue, dimensions, content goals, audience, key constraints…"
                className="w-full bg-white border border-[#E2E8F0] rounded-md px-4 py-3 text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#00439C] focus:ring-2 focus:ring-[#00439C]/15 transition resize-none"
              />
            </div>

            <div className="mt-7 flex items-center justify-between gap-4 flex-wrap">
              <div className="text-xs text-[#64748B]">
                By submitting you agree to our <span className="underline decoration-dotted">privacy policy</span>.
              </div>
              <button
                type="submit"
                data-testid="quote-submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#00439C] text-white text-sm font-semibold rounded-md hover:bg-[#003278] transition disabled:opacity-50 shadow-sm"
              >
                {submitting ? "Sending…" : "Send brief"}
                <Send size={15} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const Label = ({ children }) => (
  <label className="block text-[13px] font-semibold text-[#0F172A] mb-2">{children}</label>
);

const Field = ({ label, testid, value, onChange, type = "text" }) => (
  <div>
    <Label>{label}</Label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={onChange}
      className="w-full bg-white border border-[#E2E8F0] rounded-md px-4 py-2.5 text-[#0F172A] focus:outline-none focus:border-[#00439C] focus:ring-2 focus:ring-[#00439C]/15 transition"
    />
  </div>
);

const Select = ({ label, testid, value, onChange, options }) => (
  <div>
    <Label>{label}</Label>
    <select
      data-testid={testid}
      value={value}
      onChange={onChange}
      className="w-full bg-white border border-[#E2E8F0] rounded-md px-4 py-2.5 text-[#0F172A] focus:outline-none focus:border-[#00439C] focus:ring-2 focus:ring-[#00439C]/15 transition appearance-none"
    >
      <option value="">Select…</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </div>
);

const ContactItem = ({ icon: Icon, label, value, href, external }) => {
  const Wrapper = href ? "a" : "div";
  const extraProps = external ? { target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <Wrapper href={href} {...extraProps} className="flex items-start gap-4 group">
      <div className="w-10 h-10 rounded-lg bg-white border border-[#E2E8F0] flex items-center justify-center flex-shrink-0 group-hover:bg-[#00439C] group-hover:border-[#00439C] transition">
        <Icon size={18} className="text-[#00439C] group-hover:text-white transition" />
      </div>
      <div>
        <div className="text-[12px] uppercase tracking-wider font-semibold text-[#64748B] mb-0.5">{label}</div>
        <div className="text-[15px] font-semibold text-[#0F172A] group-hover:text-[#00439C] transition">{value}</div>
      </div>
    </Wrapper>
  );
};

export default QuoteForm;
